/** @file 2x3 affine matrices [a, b, c, d, tx, ty]: columns are the X axis (a,c), Y axis (b,d) and origin. */
import { angle } from './angle';

export type Affine = Float64Array | number[];
/** [x, y, rotation, scaleX, scaleY, shearX, shearY] in px and degrees. */
export type LocalTransformArray = Float64Array | number[];

const DEG = Math.PI / 180;

export const affine = {
  /** Writes the identity into out. */
  identity(out: Affine): Affine {
    out[0] = 1; out[1] = 0; out[2] = 0; out[3] = 1; out[4] = 0; out[5] = 0;
    return out;
  },
  /** out = p * l (parent then local). Safe when out aliases p or l. */
  mul(out: Affine, p: Affine, l: Affine): Affine {
    const pa = p[0] ?? 0, pb = p[1] ?? 0, pc = p[2] ?? 0, pd = p[3] ?? 0, px = p[4] ?? 0, py = p[5] ?? 0;
    const la = l[0] ?? 0, lb = l[1] ?? 0, lc = l[2] ?? 0, ld = l[3] ?? 0, lx = l[4] ?? 0, ly = l[5] ?? 0;
    out[0] = pa * la + pb * lc;
    out[1] = pa * lb + pb * ld;
    out[2] = pc * la + pd * lc;
    out[3] = pc * lb + pd * ld;
    out[4] = pa * lx + pb * ly + px;
    out[5] = pc * lx + pd * ly + py;
    return out;
  },
  /** out = inverse of m. A singular matrix yields Infinity/NaN entries; callers validate scale != 0. */
  invert(out: Affine, m: Affine): Affine {
    const a = m[0] ?? 0, b = m[1] ?? 0, c = m[2] ?? 0, d = m[3] ?? 0, tx = m[4] ?? 0, ty = m[5] ?? 0;
    const det = a * d - b * c;
    const ia = d / det, ib = -b / det, ic = -c / det, id = a / det;
    out[0] = ia; out[1] = ib; out[2] = ic; out[3] = id;
    out[4] = -(ia * tx + ib * ty);
    out[5] = -(ic * tx + id * ty);
    return out;
  },
  /** Transforms a point; writes [x', y'] into out. */
  apply(m: Affine, x: number, y: number, out: [number, number]): [number, number] {
    out[0] = (m[0] ?? 0) * x + (m[1] ?? 0) * y + (m[4] ?? 0);
    out[1] = (m[2] ?? 0) * x + (m[3] ?? 0) * y + (m[5] ?? 0);
    return out;
  },
  /** Transforms a direction (no translation); writes into out. */
  applyVec(m: Affine, x: number, y: number, out: [number, number]): [number, number] {
    out[0] = (m[0] ?? 0) * x + (m[1] ?? 0) * y;
    out[1] = (m[2] ?? 0) * x + (m[3] ?? 0) * y;
    return out;
  },
  /** Builds the local matrix from the per-axis polar parameterization (thetaX = rot+shearX, thetaY = rot+90+shearY). */
  fromLocal(out: Affine, x: number, y: number, rotation: number, scaleX: number, scaleY: number, shearX: number, shearY: number): Affine {
    const tx = (rotation + shearX) * DEG;
    const ty = (rotation + 90 + shearY) * DEG;
    out[0] = Math.cos(tx) * scaleX;
    out[2] = Math.sin(tx) * scaleX;
    out[1] = Math.cos(ty) * scaleY;
    out[3] = Math.sin(ty) * scaleY;
    out[4] = x;
    out[5] = y;
    return out;
  },
  /** Exact inverse of fromLocal (shearX = 0; a reflection becomes negative scaleY rather than +-180 deg shear). */
  decompose(m: Affine, out: LocalTransformArray): LocalTransformArray {
    const a = m[0] ?? 0, b = m[1] ?? 0, c = m[2] ?? 0, d = m[3] ?? 0;
    const rotation = Math.atan2(c, a) / DEG;
    const thetaY = Math.atan2(d, b) / DEG;
    let scaleY = Math.hypot(b, d);
    let shearY = angle.wrap180(thetaY - rotation - 90);
    if (a * d - b * c < 0) {
      scaleY = -scaleY;
      shearY = angle.wrap180(shearY - 180);
    }
    out[0] = m[4] ?? 0; out[1] = m[5] ?? 0; out[2] = rotation; out[3] = Math.hypot(a, c);
    out[4] = scaleY; out[5] = 0; out[6] = shearY;
    return out;
  },
};
